import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  ChevronDown, ChevronUp, CheckCircle2, AlertTriangle, MapPin, Cake,
  MessageSquare, Copy as CopyIcon, PhoneOutgoing, Zap, Bot, Plus, Check, X, Pencil, Trash2,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { apiPost, apiPut, apiDel } from '../../lib/api.js';
import { useAuth } from '../../store/auth.js';
import { fmtDate, fmtFromNow, fmtPhone } from '../../lib/format.js';
import { formatCustomFields } from '../../lib/leadCustomFields.js';
import ClickToCall from '../../components/ClickToCall.jsx';
import LeadDrawer from '../../components/LeadDrawer.jsx';
import { openPhonePanel, setActiveCallGlobal } from '../../components/PhonePanel.jsx';
import * as tv from '../../lib/twilioVoice.js';
import { BOT_DISPOSITION_OPTIONS, useLocalTime } from './leadsConstants.jsx';
import { formatLeadCost } from '../../lib/leadCost.js';
import useQuickSale from '../../components/QuickSaleModal.jsx';

export default function LeadRow({ lead, dispositions = [], drips = [], checked, onCheck, onOpen, onDispositionApplied }) {
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState(false);
  const [showDispoMenu, setShowDispoMenu] = useState(false);
  const { askSale, saleModal } = useQuickSale();

  const tags = (lead.dispositionTags || [])
    .map((slug) => dispositions.find((d) => d.slug === slug))
    .filter(Boolean);

  /**
   * Apply a disposition; a Sold disposition first prompts for annual premium
   * and aborts if the prompt is cancelled.
   * @param {string} dispositionId
   */
  const apply = async (dispositionId) => {
    try {
      const selected = dispositions.find((d) => d._id === dispositionId);
      const isSold = selected?.systemCode === 'SOLD'
        || selected?.slug === 'sold'
        || /sold/i.test(selected?.label || '');
      const payload = { leadId: lead._id, dispositionId };
      if (isSold) {
        setShowDispoMenu(false);
        const sale = await askSale();
        if (sale == null) return;
        payload.annualPremium = sale.annualPremium;
        payload.firstDraftDate = sale.firstDraftDate;
      }
      const applied = await apiPost('/dispositions/apply', payload);
      if (applied?.warning) toast(applied.warning, { icon: '⚠️', duration: 8000 });
      toast.success(isSold ? 'Lead marked as Sold and commission updated' : 'Disposition applied');
      setShowDispoMenu(false);
      onDispositionApplied?.();
      qc.invalidateQueries({ queryKey: ['leads'] });
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to apply');
    }
  };

  const { agent, isAdmin } = useAuth();
  const assignedId = String(lead.assignedAgent?._id || lead.assignedAgent || '');
  const canDelete = isAdmin() || (!!agent?._id && assignedId === String(agent._id));

  /** Confirm, delete the lead, and refresh the table. Pool leads are rejected by the API. */
  const deleteLead = async () => {
    if (!window.confirm(`Delete lead "${lead.firstName || 'Lead'}"?`)) return;
    try {
      await apiDel(`/leads/${lead._id}`);
      toast.success('Lead deleted');
      qc.invalidateQueries({ queryKey: ['leads'] });
      onDispositionApplied?.();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to delete lead');
    }
  };

  const dispositionToBot = async (dispositionLabel) => {
    if (!window.confirm(`This will text ${lead.firstName || 'this lead'} now with the ${dispositionLabel}. Continue?`)) return;
    try {
      const res = await apiPost(`/leads/${lead._id}/disposition-to-bot`, { dispositionLabel });
      const outcome = res?.result?.outcome;
      if (outcome === 'sent' || outcome === 'override-logged') toast.success('Booking message sent');
      else toast.error(`Not sent: ${res?.result?.reason || outcome || 'unknown'}`);
      setShowDispoMenu(false);
      onDispositionApplied?.();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to disposition to bot');
    }
  };

  const enrollDrip = async (dripGroupId) => {
    try {
      const res = await apiPost('/leads/bulk/enroll-drip', { leadIds: [lead._id], dripGroupId });
      const enrolled = res?.enrolled ?? 0;
      const already = res?.alreadyEnrolled ?? 0;
      if (enrolled > 0) toast.success('Lead added to drip');
      else if (already > 0) toast('Already enrolled in this drip', { icon: 'ℹ️' });
      else toast.error('Could not add lead to drip');
      setShowDispoMenu(false);
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to enroll');
    }
  };

  return (
    <div className="hover:bg-white/2 transition">
      <div className="px-4 py-3 flex items-start gap-3">
        <input
          type="checkbox"
          checked={checked}
          onChange={onCheck}
          className="w-4 h-4 mt-1 accent-civix-purple"
        />

        <button
          onClick={() => setExpanded((e) => !e)}
          className="mt-0.5 w-7 h-7 rounded-full bg-civix-purple/20 hover:bg-civix-purple/40 border border-civix-purple/40 flex items-center justify-center text-civix-purpleSoft transition shrink-0"
          title={expanded ? 'Collapse' : 'Expand'}
        >
          {expanded
            ? <ChevronUp className="w-4 h-4" />
            : <ChevronDown className="w-4 h-4" />}
        </button>

        <div className="flex-1 min-w-0 grid grid-cols-1 md:grid-cols-[1.4fr_1.3fr_0.9fr_1.1fr_1.1fr] gap-4">
          <RowIdentity lead={lead} tags={tags} onOpenName={onOpen} onDelete={canDelete ? deleteLead : null} />
          <RowNotes lead={lead} qc={qc} />
          <RowActions
            lead={lead}
            dispositions={dispositions}
            drips={drips}
            showDispoMenu={showDispoMenu}
            setShowDispoMenu={setShowDispoMenu}
            apply={apply}
            enrollDrip={enrollDrip}
            dispositionToBot={dispositionToBot}
          />
          <RowDetail lead={lead} qc={qc} />
          <RowStatus lead={lead} qc={qc} />
        </div>
      </div>

      {expanded && (
        <div className="px-6 pb-4 pt-1 border-t border-white/5 bg-white/1.5">
          <InlineLeadDrawer leadId={lead._id} onClose={() => setExpanded(false)} />
        </div>
      )}
      {saleModal}
    </div>
  );
}

function PhonePopoutButton({ phone, leadId }) {
  if (!phone) return null;
  const qc = useQueryClient();
  const handleClick = async (e) => {
    e.stopPropagation();
    try {
      openPhonePanel();
      const res = await apiPost('/phone/click-to-call', { phone, leadId });
      if (!res?.ok) {
        toast.error(res?.error || 'Call failed');
        return;
      }
      qc.invalidateQueries({ queryKey: ['call-history'] });
      qc.invalidateQueries({ queryKey: ['phone-calls'] });
      if (leadId) {
        qc.invalidateQueries({ queryKey: ['lead', leadId] });
      }

      const { mode, token, callContext } = res.data || {};
      if (callContext?.callId) {
        setActiveCallGlobal({
          callId: callContext.callId,
          callSid: callContext.callSid || null,
          leadName: callContext.leadName || '',
          leadId: callContext.leadId || null,
          email: callContext.leadEmail || '',
          fromNumber: callContext.from || '',
          callState: 'ringing',
        });
      }

      if (mode === 'browser' && token) {
        try {
          if (!tv.isReady()) await tv.initVoice(token);
          await tv.placeCall(callContext?.to || phone, { callId: callContext?.callId || '' });
          toast.success(`Calling ${phone}…`);
        } catch (voiceErr) {
          console.warn('Voice connect warning:', voiceErr);
          toast.success(`Call initiated to ${phone}`);
        }
      } else if (mode === 'cell') {
        toast.success('Calling your cell to bridge…');
      }
    } catch (err) {
      toast.error(err?.response?.data?.error || err?.message || 'Call failed');
    }
  };
  return (
    <button
      onClick={handleClick}
      className="text-civix-purpleSoft hover:text-civix-cyan transition"
      title={`Quick dial ${phone}`}
      aria-label={`Quick dial ${phone}`}
    >
      <PhoneOutgoing className="w-3.5 h-3.5" />
    </button>
  );
}

function RowIdentity({ lead, tags = [], onOpenName, onDelete = null }) {
  const [copied, setCopied] = useState(false);
  const localTime = useLocalTime(lead.timezone);
  const copyPhone = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(lead.phone || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  };
  return (
    <div className="min-w-0">
      <button
        onClick={onOpenName}
        className="text-left font-semibold text-white hover:text-civix-cyan transition truncate block"
      >
        {lead.firstName} {lead.lastName}
        {lead.agedPool?.enrolled && lead.lockedTo?.agent && (
          <span
            className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded-sm text-[10px] font-bold bg-amber-500/20 text-amber-300 border border-amber-500/30 align-middle"
            title="This lead is also in the Aged Lead Pool and locked to a teammate"
          >
            In Progress
          </span>
        )}
      </button>

      <div className="flex items-center gap-1.5">
        <button
          onClick={onOpenName}
          className="text-civix-purpleSoft text-sm tabular-nums hover:text-civix-cyan transition"
          title="Open lead"
        >
          {fmtPhone(lead.phone)}
        </button>
        <button
          onClick={copyPhone}
          className="text-white/40 hover:text-civix-cyan transition"
          title="Copy phone"
        >
          {copied ? <CheckCircle2 className="w-3 h-3 text-emerald-400" /> : <CopyIcon className="w-3 h-3" />}
        </button>
        <PhonePopoutButton phone={lead.phone} leadId={lead._id} />
      </div>

      {lead.email && <div className="text-xs text-white/50 truncate">{lead.email}</div>}

      <div className="flex items-center gap-1 text-[11px] text-white/50 mt-0.5">
        <span>{lead.state || '—'}</span>
        {lead.state && <MapPin className="w-3 h-3 text-civix-purple" />}
      </div>

      <div className="flex items-center gap-2 text-[10px] text-white/40 mt-1">
        <span className="uppercase tracking-wider">Birthday</span>
        <span>{lead.birthday ? fmtDate(lead.birthday).split(' ').slice(0, 3).join(' ') : '—'}</span>
        <Cake className="w-3 h-3 text-civix-purple/60" />
      </div>

      <InlineEditField
        leadId={lead._id}
        field="quote"
        label="Quote"
        value={lead.quote || ''}
      />

      <div className="text-[11px] text-white/50 mt-1">
        Local time: <span className="text-white/80">{localTime}</span>
      </div>

      <a
        href={`/inbox?lead=${lead._id}`}
        className="inline-flex items-center gap-1 text-[11px] text-civix-purpleSoft hover:text-civix-cyan mt-1"
      >
        <MessageSquare className="w-3 h-3" /> SEND SMS
      </a>
      {onDelete && (
        <button
          onClick={onDelete}
          className="ml-3 inline-flex items-center gap-1 text-[11px] text-rose-400 hover:text-rose-300 mt-1"
          title="Delete lead"
          aria-label="Delete lead"
        >
          <Trash2 className="w-3 h-3" /> DELETE
        </button>
      )}

      {tags.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1.5">
          {tags.map((d) => (
            <span
              key={d.slug}
              className="text-[10px] font-medium rounded-sm px-1.5 py-0.5 border"
              style={{
                backgroundColor: `${d.color}25`,
                color: d.color,
                borderColor: `${d.color}50`,
              }}
            >{d.label}</span>
          ))}
        </div>
      )}
    </div>
  );
}

function RowNotes({ lead, qc }) {
  const [val, setVal] = useState(lead.quickNote || '');
  const [savingState, setSavingState] = useState('idle');
  const initialRef = useRef(lead.quickNote || '');
  const timerRef = useRef(null);

  useEffect(() => {
    if (savingState === 'idle' && (lead.quickNote || '') !== initialRef.current) {
      setVal(lead.quickNote || '');
      initialRef.current = lead.quickNote || '';
    }
  }, [lead.quickNote]); // eslint-disable-line

  const save = async (next) => {
    setSavingState('saving');
    try {
      await apiPut(`/leads/${lead._id}`, { quickNote: next });
      initialRef.current = next;
      setSavingState('saved');
      setTimeout(() => setSavingState((s) => (s === 'saved' ? 'idle' : s)), 1500);
      qc.invalidateQueries({ queryKey: ['leads'] });
    } catch {
      setSavingState('error');
      toast.error('Failed to save note');
    }
  };

  const onChange = (e) => {
    const next = e.target.value;
    setVal(next);
    setSavingState('typing');
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => save(next), 1200);
  };

  const onBlur = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (val !== initialRef.current) save(val);
  };

  return (
    <div className="flex flex-col">
      <textarea
        value={val}
        onChange={onChange}
        onBlur={onBlur}
        placeholder="Enter additional notes here…"
        rows={4}
        className="w-full bg-civix-navy/60 border border-white/10 rounded-lg px-3 py-2 text-xs text-white/90 placeholder-white/30 resize-none focus:border-civix-purple focus:outline-hidden transition"
      />
      <div className="text-[10px] mt-0.5 h-3 flex items-center gap-1">
        {savingState === 'typing' && <span className="text-white/40">… typing</span>}
        {savingState === 'saving' && <span className="text-civix-cyan">Saving…</span>}
        {savingState === 'saved' && <span className="text-emerald-400 flex items-center gap-1"><CheckCircle2 className="w-2.5 h-2.5" /> Saved</span>}
        {savingState === 'error' && <span className="text-red-400 flex items-center gap-1"><AlertTriangle className="w-2.5 h-2.5" /> Save failed</span>}
      </div>
    </div>
  );
}

function RowActions({ lead, dispositions = [], drips = [], showDispoMenu, setShowDispoMenu, apply, enrollDrip, dispositionToBot }) {
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 224, openUp: false });

  const updatePosition = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const menuH = 280;
    const menuW = 224;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUp = spaceBelow < menuH && rect.top > spaceBelow;
    let left = rect.right - menuW;
    if (left < 8) left = 8;
    if (left + menuW > window.innerWidth - 8) left = window.innerWidth - menuW - 8;
    setPos({
      top: openUp ? rect.top - 4 : rect.bottom + 4,
      left,
      width: menuW,
      openUp,
    });
  };

  useLayoutEffect(() => {
    if (showDispoMenu) updatePosition();
  }, [showDispoMenu]);

  useEffect(() => {
    if (!showDispoMenu) return;
    const onScrollOrResize = () => updatePosition();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [showDispoMenu]);

  useEffect(() => {
    if (!showDispoMenu) return;
    function onDoc(e) {
      const t = e.target;
      if (triggerRef.current?.contains(t)) return;
      if (menuRef.current?.contains(t)) return;
      setShowDispoMenu(false);
    }
    function onKey(e) {
      if (e.key === 'Escape') setShowDispoMenu(false);
    }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [showDispoMenu, setShowDispoMenu]);

  const menuStyle = {
    position: 'fixed',
    top: pos.openUp ? undefined : pos.top,
    bottom: pos.openUp ? window.innerHeight - pos.top : undefined,
    left: pos.left,
    width: pos.width,
    zIndex: 9999,
  };

  return (
    <div className="flex flex-col gap-1.5">
      <ClickToCall phone={lead.phone} leadId={lead._id} callCount={lead.callCount || 0} className="w-full justify-center py-2" />
      <div className="relative">
        <button
          ref={triggerRef}
          onClick={() => setShowDispoMenu((o) => !o)}
          className="w-full btn-ghost text-xs flex items-center justify-center gap-1.5 py-2"
        >
          <ChevronDown className="w-3.5 h-3.5" /> DISPOSITION
        </button>
        {showDispoMenu && createPortal(
          <div
            ref={menuRef}
            style={menuStyle}
            className="bg-[#0a0a1f] border border-white/15 ring-1 ring-black/40 rounded-lg shadow-[0_20px_60px_rgba(0,0,0,0.6)] overflow-hidden"
            role="menu"
          >
            <div className="max-h-[320px] overflow-y-auto py-1">
              {dispositions.map((d) => (
                <button
                  key={d._id}
                  role="menuitem"
                  className="w-full text-left px-3 py-1.5 text-xs hover:bg-white/5 flex items-center gap-2"
                  onClick={() => apply(d._id)}
                >
                  <span className="w-2 h-2 rounded-full" style={{ background: d.color }} />
                  {d.label}
                </button>
              ))}
              <div className="mt-1 pt-1 border-t border-white/10">
                <div className="px-3 py-1 text-[10px] uppercase tracking-wider text-white/40">
                  Add to drip
                </div>
                {drips.length === 0 && (
                  <div className="px-3 py-1.5 text-[11px] text-white/40">
                    No drips yet — create one in Marketing → Drip Builder.
                  </div>
                )}
                {drips.map((d) => (
                  <button
                    key={d._id}
                    role="menuitem"
                    data-testid="row-add-to-drip"
                    className="w-full text-left px-3 py-1.5 text-xs hover:bg-white/5 flex items-center gap-2"
                    onClick={() => enrollDrip?.(d.dripGroupId || d._id)}
                  >
                    <Zap className="w-3 h-3 text-civix-purpleSoft" />
                    {d.name}
                  </button>
                ))}
              </div>
              <div className="mt-1 pt-1 border-t border-white/10">
                <div className="px-3 py-1 text-[10px] uppercase tracking-wider text-white/40">
                  Disposition to AI bot
                </div>
                {BOT_DISPOSITION_OPTIONS.map((o) => (
                  <button
                    key={o.label}
                    role="menuitem"
                    data-testid="row-disposition-to-bot"
                    className="w-full text-left px-3 py-1.5 text-xs hover:bg-white/5 flex items-center gap-2"
                    onClick={() => dispositionToBot?.(o.label)}
                  >
                    <Bot className="w-3 h-3 text-civix-purpleSoft" />
                    {o.product}
                  </button>
                ))}
              </div>
            </div>
          </div>,
          document.body
        )}
      </div>
    </div>
  );
}

function RowDetail({ lead, qc }) {
  const [showAdd, setShowAdd] = useState(false);
  const customFields = lead.customFields
    ? (lead.customFields instanceof Map
        ? Object.fromEntries(lead.customFields)
        : lead.customFields)
    : {};

  return (
    <div className="text-xs text-white/70 space-y-1">
      <DetailRow leadId={lead._id} field="mortgageAmount" label="Mortgage amount" value={lead.mortgageAmount} />
      <DetailRow leadId={lead._id} field="ageRange" label="Age range" value={lead.ageRange} />
      <DetailRow leadId={lead._id} field="beneficiary" label="Beneficiary" value={lead.beneficiary} />
      <DetailRow leadId={lead._id} field="protectionGoal" label="Protection goal" value={lead.protectionGoal} />

      {formatCustomFields(customFields, { productInterest: lead.productInterest }).map(({ key, label, value }) => (
        <div key={key} className="flex items-baseline gap-1">
          <span className="text-white/50">{label}:</span>
          <span className="text-white/90">{value}</span>
        </div>
      ))}

      {showAdd ? (
        <NewCustomFieldInput
          leadId={lead._id}
          existing={customFields}
          onDone={() => {
            setShowAdd(false);
            qc.invalidateQueries({ queryKey: ['leads'] });
          }}
        />
      ) : (
        <button
          onClick={() => setShowAdd(true)}
          className="mt-1 inline-flex items-center gap-1 text-[10px] text-civix-purpleSoft hover:text-civix-cyan border border-civix-purple/40 rounded-sm px-1.5 py-0.5"
        >
          <Plus className="w-2.5 h-2.5" /> NEW FIELD
        </button>
      )}
    </div>
  );
}

function DetailRow({ leadId, field, label, value }) {
  return (
    <div className="flex items-baseline gap-1">
      <span className="text-white/50 shrink-0">{label}:</span>
      <InlineEditField leadId={leadId} field={field} value={value || ''} placeholder="Not set" inline />
    </div>
  );
}

function NewCustomFieldInput({ leadId, existing = {}, onDone }) {
  const [k, setK] = useState('');
  const [v, setV] = useState('');
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!k.trim()) return onDone();
    setSaving(true);
    try {
      await apiPut(`/leads/${leadId}`, {
        customFields: { ...existing, [k.trim()]: v },
      });
      toast.success('Field added');
      onDone();
    } catch {
      toast.error('Failed to add field');
      setSaving(false);
    }
  };
  return (
    <div className="mt-1 flex items-center gap-1">
      <input
        autoFocus
        value={k}
        onChange={(e) => setK(e.target.value)}
        placeholder="Field name"
        className="flex-1 min-w-0 bg-civix-navy/60 border border-civix-purple/40 rounded-sm px-1.5 py-0.5 text-[11px] text-white"
      />
      <input
        value={v}
        onChange={(e) => setV(e.target.value)}
        placeholder="Value"
        className="flex-1 min-w-0 bg-civix-navy/60 border border-civix-purple/40 rounded-sm px-1.5 py-0.5 text-[11px] text-white"
        onKeyDown={(e) => e.key === 'Enter' && save()}
      />
      <button onClick={save} disabled={saving} className="text-emerald-400 hover:text-emerald-300">
        <Check className="w-3.5 h-3.5" />
      </button>
      <button onClick={onDone} className="text-white/40 hover:text-white/80">
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

function RowStatus({ lead }) {
  const tier = lead.tier;
  const vendorName = lead.vendor?.name || lead.leadVendorName || lead.leadType || '';
  return (
    <div className="text-xs text-white/60 space-y-1">
      <div>
        <span className="text-white/50">Received on:</span>
        <div className="text-white/90">{fmtDate(lead.createdAt)}</div>
      </div>
      {(vendorName || tier) && (
        <div className="text-white/80 font-medium text-[11px]">
          {vendorName}{vendorName && tier && ' • '}{tier}
        </div>
      )}
      <DetailRow leadId={lead._id} field="saleAmount" label="Sale amount" value={lead.saleAmount ? `$${Number(lead.saleAmount).toLocaleString()}` : ''} />
      <DetailRow leadId={lead._id} field="commission" label="Commission" value={lead.commission ? `$${Number(lead.commission).toLocaleString()}` : ''} />
      <DetailRow leadId={lead._id} field="cost" label="Cost for lead" value={formatLeadCost(lead)} />
      <CallStatusBadge lead={lead} />
    </div>
  );
}

function CallStatusBadge({ lead }) {
  if (lead.lastContactedAt) {
    return (
      <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-[10px]">
        <CheckCircle2 className="w-3 h-3" /> Last called {fmtFromNow(lead.lastContactedAt)}
      </div>
    );
  }
  return (
    <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm bg-red-500/15 border border-red-500/30 text-red-300 text-[10px]">
      <AlertTriangle className="w-3 h-3" /> Not called
    </div>
  );
}

function InlineEditField({ leadId, field, value, label, placeholder = 'Not set', inline = false }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(value || '');
  useEffect(() => setV(value || ''), [value]);

  const save = async () => {
    if (v === (value || '')) { setEditing(false); return; }
    try {
      let payloadVal = v;
      if (['saleAmount', 'commission', 'cost', 'annualPremium'].includes(field)) {
        const num = Number(String(v).replace(/[^0-9.\-]/g, ''));
        payloadVal = Number.isFinite(num) ? num : 0;
      }
      await apiPut(`/leads/${leadId}`, { [field]: payloadVal });
      qc.invalidateQueries({ queryKey: ['leads'] });
      setEditing(false);
    } catch {
      toast.error('Save failed');
    }
  };

  if (editing) {
    return (
      <input
        autoFocus
        value={v}
        onChange={(e) => setV(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => {
          if (e.key === 'Enter') save();
          if (e.key === 'Escape') { setV(value || ''); setEditing(false); }
        }}
        className="bg-civix-navy/60 border border-civix-purple/60 rounded-sm px-1.5 py-0.5 text-[11px] text-white min-w-0 flex-1"
      />
    );
  }

  if (inline) {
    return (
      <button
        onClick={() => setEditing(true)}
        className={`text-left hover:text-civix-cyan transition truncate ${value ? 'text-white/90' : 'text-white/40 italic'}`}
      >
        {value || placeholder}
      </button>
    );
  }

  return (
    <div className="flex items-center gap-1 text-[11px] mt-0.5">
      {label && <span className="text-white/50">{label}:</span>}
      <button
        onClick={() => setEditing(true)}
        className={`hover:text-civix-cyan transition ${value ? 'text-white/90' : 'text-white/40 italic'}`}
      >
        {value || placeholder}
      </button>
      <Pencil className="w-2.5 h-2.5 text-civix-purple/60" />
    </div>
  );
}

function InlineLeadDrawer({ leadId, onClose }) {
  return (
    <div className="rounded-xl border border-civix-purple/30 bg-civix-navy/50 p-4">
      <LeadDrawer leadId={leadId} onClose={onClose} inline />
    </div>
  );
}
